import { Button, Chip, Input, Textarea } from '@emailforge/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type ImportDiagnostic,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useImportTemplateMutation,
  useListTemplatesQuery,
} from '@/features/templates/templatesApi';
import { t } from '@/lib/i18n';

export function TemplatesScreen() {
  const navigate = useNavigate();
  const { data, isLoading } = useListTemplatesQuery();
  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();
  const [query, setQuery] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const list = (data ?? []).filter((t) => t.name.toLowerCase().includes(query.toLowerCase()));

  const handleCreate = async () => {
    const name = window.prompt('Template name', 'Untitled email');
    if (!name) return;
    const res = await createTemplate({ name }).unwrap();
    navigate(`/app/templates/${res.id}`);
  };

  return (
    <>
      <TopBar
        crumbs={[t('nav.templates')]}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <Input
                
                placeholder="Search templates…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ width: 260, paddingLeft: 28 }} />
              <div style={{ position: 'absolute', left: 8, top: 7, color: 'var(--ink-3)' }}>
                <Icon.search size={13} />
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/app/gallery')}>
              <Icon.folder size={13} /> Gallery
            </Button>
            <Button size="sm" onClick={() => setImportOpen(true)}>
              <Icon.upload size={13} /> Import .tsx
            </Button>
            <Button variant="flame" size="sm" onClick={handleCreate} disabled={isCreating}>
              <Icon.plus size={13} /> {isCreating ? 'Creating…' : 'New template'}
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
        {isLoading && <EmptyState loading />}
        {!isLoading && list.length === 0 && <EmptyState onCreate={handleCreate} />}

        {!isLoading && list.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            {list.map((t) => (
              <div
                key={t.id}
                className="mf-card"
                style={{ padding: 0, cursor: 'pointer', overflow: 'hidden' }}
                onClick={() => navigate(`/app/templates/${t.id}`)}
              >
                <div
                  style={{
                    height: 160,
                    background: 'var(--paper-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 0.06, textTransform: 'uppercase' }}
                  >
                    Preview
                  </span>
                </div>
                <div style={{ padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 14, fontWeight: 550, flex: 1 }}>{t.name}</div>
                    <Chip variant={t.status === 'published' ? 'live' : 'draft'}>{t.status}</Chip>
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
                  >
                    v{t.latestVersionNumber} · updated {new Date(t.updatedAt).toLocaleDateString()}
                  </div>
                  <div
                    style={{
                      marginTop: 10,
                      display: 'flex',
                      gap: 6,
                      justifyContent: 'flex-end',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant="ghost" size="sm" onClick={async () => {
                        if (!window.confirm('Archive this template?')) return;
                        await deleteTemplate(t.id).unwrap();
                      }}
                      aria-label="Archive"
                    >
                      <Icon.trash size={12} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} />}
    </>
  );
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [importTemplate, { isLoading }] = useImportTemplateMutation();
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [diagnostics, setDiagnostics] = useState<ImportDiagnostic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    if (!file.name.match(/\.(tsx|jsx)$/i)) {
      setError('Please select a .tsx or .jsx file.');
      return;
    }
    const text = await file.text();
    setSource(text);
    if (!name) setName(file.name.replace(/\.(tsx|jsx)$/i, '').replace(/[-_]/g, ' '));
  };

  const onSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!source.trim()) {
      setError('Paste code or upload a file.');
      return;
    }
    try {
      const res = await importTemplate({ name: name.trim(), source }).unwrap();
      setDiagnostics(res.diagnostics);
      if (res.diagnostics.some((d) => d.severity === 'error')) return;
      onClose();
      navigate(`/app/templates/${res.id}`);
    } catch (e: unknown) {
      setError((e as { data?: { message?: string } })?.data?.message ?? 'Import failed.');
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 40,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 640,
          maxWidth: '92vw',
          maxHeight: '86vh',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-3)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--line-soft)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Icon.upload size={15} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>Import React Email template</div>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" icon onClick={onClose} aria-label="Close">
            <Icon.x size={12} />
          </Button>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                color: 'var(--ink-3)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Template name
            </label>
            <Input
              
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Welcome email" />
          </div>

          <div>
            <label
              className="mono"
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                color: 'var(--ink-3)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Paste .tsx source (or upload a file)
            </label>
            <input
              type="file"
              accept=".tsx,.jsx,text/plain"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }}
              style={{ marginBottom: 8, fontSize: 12 }}
            />
            <Textarea mono
              
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder={'import { Hero, Heading, Paragraph } from "@emailforge/email-blocks";\n\nexport default function Welcome() {\n  return (\n    <Hero title="Welcome" />\n  );\n}'}
              style={{ minHeight: 200, fontSize: 12, fontFamily: 'var(--font-mono)', resize: 'vertical' }} />
          </div>

          {diagnostics && diagnostics.length > 0 && (
            <div
              style={{
                padding: 12,
                background: 'var(--paper-2)',
                border: '1px solid var(--line-soft)',
                borderRadius: 'var(--r-2)',
                fontSize: 12.5,
              }}
            >
              <div style={{ fontWeight: 550, marginBottom: 6 }}>Parser diagnostics</div>
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {diagnostics.map((d, i) => (
                  <li
                    key={i}
                    style={{
                      color: d.severity === 'error' ? 'var(--err)' : 'var(--warn)',
                      marginBottom: 2,
                    }}
                  >
                    {d.severity === 'error' ? '✗' : '⚠'}{' '}
                    {d.line != null && <span className="mono">L{d.line}: </span>}
                    {d.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div
              style={{
                padding: 10,
                background: 'var(--flame-soft)',
                border: '1px solid var(--flame-line)',
                borderRadius: 'var(--r-2)',
                color: 'var(--flame-2)',
                fontSize: 12.5,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--line-soft)',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button variant="flame" onClick={onSubmit} disabled={isLoading}>
            {isLoading ? 'Importing…' : 'Import'}
          </Button>
        </div>
      </div>
    </>
  );
}

function EmptyState({ loading, onCreate }: { loading?: boolean; onCreate?: () => void }) {
  return (
    <div className="mf-card" style={{ padding: 60, textAlign: 'center', maxWidth: 480, margin: '40px auto' }}>
      {loading ? (
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          Loading templates…
        </div>
      ) : (
        <>
          <div
            className="mono"
            style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
          >
            Your templates
          </div>
          <h2 className="display" style={{ fontSize: 24, margin: '10px 0 8px' }}>
            No templates yet
          </h2>
          <p style={{ color: 'var(--ink-2)', fontSize: 13.5, margin: '0 0 20px', lineHeight: 1.5 }}>
            Start from a blank canvas or fork one of the 32 starters from the gallery.
          </p>
          <Button variant="flame" onClick={onCreate}>
            <Icon.plus size={13} /> New blank template
          </Button>
        </>
      )}
    </div>
  );
}
